import React, { useEffect, useState } from 'react';
import { recordOutreach, APPT_TYPES } from '../../services/outreach';
import { addCalendarEvent, CALENDAR_ENABLED } from '../../services/googleCalendarEvent';
import { watchGoogleIntegrations, requestCalendarSync } from '../../services/googleSync';
import { isMobileBrowser } from '../../services/googleRedirect';

const MOBILE_CONNECT_NOTE = 'Connect Google Calendar in Profile to have appointments added to Google automatically.';

// Quick "Log appointment" for My Outreach. Only records the count (users/{uid}/outreach); it does not
// create a Resi appointment, so appointments made with the full Appointment form are never counted twice.
const overlay = { position: 'fixed', inset: 0, background: 'rgba(20,24,33,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, zIndex: 1000 };
const modal = { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' };
const input = { width: '100%', padding: 10, fontSize: 16, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box', marginBottom: 10 };
const btn = { padding: '10px 16px', fontSize: 15, border: '1px solid #b8903a', background: '#d9b357', borderRadius: 6, fontWeight: 700, cursor: 'pointer' };
const localNow = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

export default function LogAppointmentModal({ uid, contact, onDone, onLogged }) {
  const [saved, setSaved] = useState(false);
  const [when, setWhen] = useState(localNow());
  const [name, setName] = useState(contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || '' : '');
  const [type, setType] = useState(APPT_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState(contact?.phone || '');
  // Phones can't use the Google popup (Safari blocks it): no direct add there, use the Profile connection instead.
  const mobile = isMobileBrowser();
  const [toCal, setToCal] = useState(CALENDAR_ENABLED && !mobile);
  const [minutes, setMinutes] = useState('60');
  const [title, setTitle] = useState('');
  const [calMsg, setCalMsg] = useState('');
  // Connected on Profile + this is a pipeline appointment: the server sync adds it to Google, so skip the popup (no duplicates).
  const [autoSync, setAutoSync] = useState(false);
  useEffect(() => (uid && onLogged ? watchGoogleIntegrations(uid, (g) => setAutoSync(g?.calendar?.status === 'connected')) : undefined), [uid, onLogged]);
  const save = async () => {
    setBusy(true); setCalMsg('');
    const apptAt = new Date(when);
    // 1) Save to Resi first. If this fails, show the error and keep the modal open.
    try {
      await recordOutreach(uid, 'appointment', 'manual', { manual: true, apptType: type, apptAt: apptAt.toISOString(), contactName: name.trim(), contactId: contact?.contactId || contact?.id });
    } catch (e) { setBusy(false); setCalMsg(`Not saved: ${e?.message || 'could not reach the database'}`); return; }
    let extra = '';
    if (onLogged) { try { extra = (await onLogged({ type, when, apptAt })) || ''; } catch (e) { extra = `Pipeline entry not updated: ${e?.message || e}`; } }
    // 2) Then try Google Calendar (with a timeout inside addCalendarEvent). Never blocks closing.
    if (autoSync) requestCalendarSync();
    if (mobile && !autoSync && CALENDAR_ENABLED) { setBusy(false); setSaved(true); setCalMsg(`Logged in Resi${extra ? ` (${extra})` : ''}. ${MOBILE_CONNECT_NOTE}`); return; }
    if (toCal && !autoSync && !mobile) {
      try { await addCalendarEvent({ title: title.trim(), start: apptAt, minutes, name: name.trim(), phone: phone.trim(), type }); }
      catch (e) { setBusy(false); setSaved(true); setCalMsg(`Logged in Resi${extra ? ` (${extra})` : ''}, but the Google Calendar event was not added: ${e?.message || e}`); return; }
    }
    setBusy(false); onDone(true, extra);
  };
  return (
    <div style={overlay} onClick={() => onDone(false)}>
      <div style={modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Log appointment">
        <h3 style={{ marginTop: 0 }}>Log appointment</h3>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Date and time</label>
        <input type="datetime-local" style={input} value={when} onChange={(e) => setWhen(e.target.value)} />
        <label style={{ fontSize: 13, fontWeight: 600 }}>Contact</label>
        <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Who is it with?" />
        <label style={{ fontSize: 13, fontWeight: 600 }}>Type</label>
        <select style={input} value={type} onChange={(e) => setType(e.target.value)}>{APPT_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Phone</label>
        <input style={input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Client phone" />
        {autoSync && <div style={{ fontSize: 13, color: '#1E7B34', marginBottom: 10 }}>This goes to your Google Calendar automatically (connected on your Profile).</div>}
        {CALENDAR_ENABLED && !autoSync && mobile && <div style={{ fontSize: 13, color: '#8C6D1F', marginBottom: 10 }}>{MOBILE_CONNECT_NOTE}</div>}
        {CALENDAR_ENABLED && !autoSync && !mobile && (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 14 }}><input type="checkbox" checked={toCal} onChange={(e) => setToCal(e.target.checked)} /> Add to my Google Calendar</label>
            {toCal && (<>
              <input style={{ ...input, marginTop: 8 }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`${type}: ${name || 'client'}`} aria-label="Event title" />
              <label style={{ fontSize: 13, fontWeight: 600 }}>Length (minutes)</label>
              <input style={input} type="number" min="5" step="5" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            </>)}
          </div>
        )}
        {calMsg && <div style={{ color: '#a33', fontSize: 13, marginBottom: 8 }}>{calMsg} <button type="button" onClick={() => onDone(true)}>Close</button></div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btn} disabled={busy || !when || saved} onClick={save}>{busy ? 'Saving...' : saved ? 'Saved' : 'Log it'}</button>
          <button type="button" style={{ ...btn, background: '#fff' }} onClick={() => onDone(saved)}>{saved ? 'Close' : 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}
